import { Controller, type UseFormReturn } from 'react-hook-form';
import type { ContentProfile } from '@content-planner/shared';
import { SingleChoice } from './ChoiceGroup.js';
import { audienceChoices, businessTypeChoices, goalChoices } from './options.js';

type FormShape = ContentProfile & { month?: string };

export function StepRound1({ form }: { form: UseFormReturn<FormShape> }) {
  const businessType = form.watch('businessType');
  const audience = form.watch('audience');

  return (
    <div>
      <h2 className="mb-1">Tell us about the business.</h2>
      <p className="text-text-secondary mb-6">
        We use this to tailor every post in your plan.
      </p>

      <Controller
        name="businessType"
        control={form.control}
        rules={{ required: 'Pick the closest fit.' }}
        render={({ field, fieldState }) => (
          <SingleChoice
            name="businessType"
            legend="What kind of business is this?"
            choices={businessTypeChoices}
            value={field.value}
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      {businessType === 'other' && (
        <label className="block mb-6">
          <span className="font-heading font-semibold text-sm">Describe your business</span>
          <input
            type="text"
            className="gcm-input mt-2"
            placeholder="e.g. a coworking space for parents"
            maxLength={200}
            {...form.register('businessTypeOther', { required: 'Add a short description.' })}
          />
          {form.formState.errors.businessTypeOther && (
            <p className="text-sm text-danger mt-1">
              {form.formState.errors.businessTypeOther.message}
            </p>
          )}
        </label>
      )}

      <Controller
        name="audience"
        control={form.control}
        rules={{ required: 'Pick the closest audience.' }}
        render={({ field, fieldState }) => (
          <SingleChoice
            name="audience"
            legend="Who are you trying to reach?"
            choices={audienceChoices}
            value={field.value}
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      {audience === 'other' && (
        <label className="block mb-6">
          <span className="font-heading font-semibold text-sm">Describe your audience</span>
          <input
            type="text"
            className="gcm-input mt-2"
            placeholder="e.g. yoga teachers running their own studio"
            maxLength={200}
            {...form.register('audienceOther', { required: 'Add a short description.' })}
          />
          {form.formState.errors.audienceOther && (
            <p className="text-sm text-danger mt-1">
              {form.formState.errors.audienceOther.message}
            </p>
          )}
        </label>
      )}

      <Controller
        name="goal"
        control={form.control}
        rules={{ required: 'Pick a primary goal.' }}
        render={({ field, fieldState }) => (
          <SingleChoice
            name="goal"
            legend="What's the main goal for this month?"
            choices={goalChoices}
            value={field.value}
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />
    </div>
  );
}
